import React from 'react';

import { dataCellClass, dataFrameClass, dataRowClass } from '@/components/admin/SysadminSPA/shared/SPADataTable';
import SPAPill, { SPAPillTone } from '@/components/admin/SysadminSPA/shared/SPAPill';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/Table';
import cn from '@/utils/cn';

import { tableHeaderCellClass, tableHeaderRowClass } from './tone';
import { MappingRow, MappingStatus } from './types';

const STATUS: Record<MappingStatus, { label: string; tone: SPAPillTone }> = {
  mapped: { label: 'Mapped', tone: 'success' },
  online_only: { label: 'Online only', tone: 'neutral' },
  print_only: { label: 'Not online yet', tone: 'accent' },
  review: { label: 'Review', tone: 'warning' },
};

const StorySide = ({ row }: { row: MappingRow }) => {
  if (row.status === 'online_only') {
    return (
      <p className="tw-m-0 tw-text-sm tw-italic tw-text-spa-secondary">
        No print story with this title — expected for online-only features.
      </p>
    );
  }

  return (
    <>
      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">{row.storyTitle}</p>
      <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-spa-secondary">
        {row.status === 'review'
          ? 'Matched a title-only label — nothing to compare.'
          : `Story ${row.storyId} · ${row.paragraphs} paragraphs · ${row.words} words`}
      </p>
    </>
  );
};

// Which print story each online section maps to — the core of the detection report. A section
// that maps to nothing (or to a bare label) is one the comparison can't vouch for. Framed like
// SPADataTable so it reads as the same surface.
const MappingTable = ({ mapping }: { mapping: MappingRow[] }) => (
  <div className={cn(dataFrameClass, 'tw-mt-3')}>
    <Table className="tw-w-full tw-border-collapse tw-text-left">
      <TableHeader className={tableHeaderRowClass}>
        <TableRow>
          <TableCell header className={tableHeaderCellClass}>Online section</TableCell>
          <TableCell header className={tableHeaderCellClass}>Match</TableCell>
          <TableCell header className={tableHeaderCellClass}>Print story</TableCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {mapping.map((row, i) => {
          const cell = cn(dataCellClass(i === mapping.length - 1), 'tw-align-top');

          return (
            <TableRow key={row.sectionId ?? row.sectionTitle} className={dataRowClass(i, false)}>
              <TableCell className={cell}>
                <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">{row.sectionTitle}</p>
                {row.sectionId !== undefined && (
                  <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-spa-secondary">ID {row.sectionId}</p>
                )}
              </TableCell>
              <TableCell className={cn(cell, 'tw-whitespace-nowrap')}>
                <SPAPill tone={STATUS[row.status].tone}>{STATUS[row.status].label}</SPAPill>
              </TableCell>
              <TableCell className={cell}><StorySide row={row} /></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export default MappingTable;
